import { useCallback, useEffect, useState } from "react";
import { HeroPanel } from "./components/HeroPanel";
import { Campaigns } from "./components/Campaigns";
import { Suppliers } from "./components/suppliers/Suppliers";
// removed health check badge
import { useAuth } from "./contexts/AuthContext";
import { Login } from "./components/Login";
import { SettingsUsers } from "./components/SettingsUsers";
import { SettingsOrg } from "./components/SettingsOrg";
import { AccountSettings } from "@/components/AccountSettings";
import { InviteAccept } from "./components/InviteAccept";
import { Register } from "./components/onboarding/Register";
import { OrganizationSetup } from "./components/onboarding/OrganizationSetup";
import { InviteMembers } from "./components/onboarding/InviteMembers";
import { OnboardingStepper, type OnboardingStepKey } from "./components/onboarding/OnboardingStepper";
import { FirstProject } from "./components/onboarding/FirstProject";
import { SupplierPortal } from "./components/SupplierPortal";
import { TemplateLibrary } from "./components/TemplateLibrary";
import { AgentsProvider } from "@/contexts/AgentsContext";
import { ChatView } from "@/components/agents/ChatView";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "./components/ui/sidebar";
import { AppSidebar } from "./components/layout/AppSidebar";
import type { ViewType } from "./components/layout/AppSidebar";
import { GlobalCommandPalette } from "@/components/search/GlobalCommandPalette";
import { SearchTrigger } from "@/components/search/SearchTrigger";
import type { SearchTarget } from "@/lib/globalSearch";

export default function App() {
  // Tous les hooks doivent être appelés AVANT tout return conditionnel (React Rules of Hooks)
  const { ready, user, logout } = useAuth();
  const [activeView, setActiveView] = useState<ViewType>('dashboard');
  // removed api health state

  // State for managing modals across components
  const [newCampaignOpen, setNewCampaignOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'organisation' | 'utilisateurs' | 'compte'>('organisation');

  // Story 4.1 + 4.2 + 4.4 — Onboarding self-service à quatre étapes
  // (Register → OrganizationSetup → InviteMembers → FirstProject).
  // `onboardingStep` est la machine à états qui guide le rendu :
  //   - 'register'      : `?register` au mount, ou user absent en phase d'inscription
  //   - 'setup'         : user vient de s'inscrire, configuration org en cours
  //   - 'invite'        : org configurée (ou skip), invitation de collaborateurs
  //   - 'first-project' : équipe invitée (ou skip), choix CTA métier (story 4.4)
  //   - null            : onboarding terminé, on rend le dashboard
  // Le guard `ready` empêche le flash du Register pour un user déjà connecté
  // qui ouvre `/?register`.
  type OnboardingStep = OnboardingStepKey | null;
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>(
    () => (new URLSearchParams(window.location.search).get('register') !== null ? 'register' : null)
  );
  // Story 4.4 — lifté ici pour que le CTA « Créer un template » du step
  // FirstProject ouvre TemplateLibrary directement en mode création.
  const [templateInitialMode, setTemplateInitialMode] = useState<'list' | 'create'>('list');
  useEffect(() => {
    if (activeView !== 'forms') {
      setTemplateInitialMode('list');
      setPendingTemplatePreviewId(null);
    }
  }, [activeView]);
  // Story 9.2 — palette de recherche globale (Cmd/Ctrl+K). `searchNavKey` force
  // le remontage de Suppliers / TemplateLibrary (état initial lu une seule fois).
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [searchNavKey, setSearchNavKey] = useState(0);
  const [pendingTemplatePreviewId, setPendingTemplatePreviewId] = useState<string | null>(null);
  const cleanUrl = () => {
    window.history.replaceState(null, '', window.location.pathname);
  };

  // Story 3.6 — Deep-link depuis le profil fournisseur vers la vue
  // campagne × fournisseur. `pendingCampaignTarget` est consommé une fois
  // par `<Campaigns>` (cf. `onTargetConsumed`) pour éviter qu'une 2e
  // navigation via le menu ne ré-applique l'auto-ouverture.
  // `supplierProfileOriginId` est mémorisé séparément : il survit tant que
  // l'utilisateur reste dans le sous-arbre Campagnes, et alimente le retour
  // contextuel vers le profil. Il est purgé au menu click (cf. handleViewChange).
  const [pendingCampaignTarget, setPendingCampaignTarget] = useState<{
    campaignId: string;
    campaignSupplierId?: string;
  } | null>(null);
  const [supplierProfileOriginId, setSupplierProfileOriginId] = useState<string | null>(null);

  // P-4 — useCallback est un hook : doit être déclaré AVANT tout return conditionnel.
  const handleNavigateToCampaign = useCallback(
    (
      campaignId: string,
      options?: { campaignSupplierId?: string; originSupplierId?: string }
    ) => {
      setPendingCampaignTarget({
        campaignId,
        campaignSupplierId: options?.campaignSupplierId,
      });
      // P-3 — toujours écraser l'origin (même si undefined) pour éviter qu'une
      // valeur précédente survive à un appel sans originSupplierId.
      setSupplierProfileOriginId(options?.originSupplierId ?? null);
      setActiveView('campaigns');
    },
    []
  );

  const handleBackToSupplierProfile = useCallback(() => {
    if (!supplierProfileOriginId) return;
    // P-8 — valider le format UUID avant d'écrire dans l'URL.
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!UUID_REGEX.test(supplierProfileOriginId)) return;
    const params = new URLSearchParams();
    params.set('view', 'profile');
    params.set('supplierId', supplierProfileOriginId);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${params.toString()}`
    );
    setPendingCampaignTarget(null);
    setSupplierProfileOriginId(null);
    setActiveView('suppliers');
  }, [supplierProfileOriginId]);

  const handleTargetConsumed = useCallback(() => {
    setPendingCampaignTarget(null);
  }, []);

  // Détecter ?invite_token AVANT l'auth gate (mais APRÈS tous les hooks)
  const inviteToken = new URLSearchParams(window.location.search).get('invite_token')
  if (inviteToken) return <InviteAccept token={inviteToken} />

  // CA-1 — Détecter ?token= pour le portail fournisseur (bypasse l'auth gate)
  const supplierToken = new URLSearchParams(window.location.search).get('token')
  if (supplierToken) return <SupplierPortal token={supplierToken} />

  // Story 4.1 + 4.2 — Onboarding gate à trois étapes. Le guard `ready` empêche
  // le flash du Register pour un user déjà authentifié qui ouvre `/?register`.
  // Une fois `user` peuplé par `apiRegisterOrg`, le ternaire bascule
  // automatiquement de Register vers OrganizationSetup — pas de flash dashboard
  // entre les deux (cf. story 4.1 review LOW).
  if (ready && onboardingStep) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full flex flex-col">
          <OnboardingStepper currentStep={onboardingStep} />
          {!user ? (
            <Register
              onSetupNeeded={() => setOnboardingStep('setup')}
              onLoginRedirect={() => {
                setOnboardingStep(null);
                cleanUrl();
              }}
            />
          ) : onboardingStep === 'register' || onboardingStep === 'setup' ? (
            <OrganizationSetup onComplete={() => setOnboardingStep('invite')} />
          ) : onboardingStep === 'invite' ? (
            <InviteMembers onComplete={() => setOnboardingStep('first-project')} />
          ) : (
            <FirstProject
              onCreateTemplate={() => {
                setOnboardingStep(null);
                cleanUrl();
                setTemplateInitialMode('create');
                setActiveView('forms');
              }}
              onExploreDashboard={() => {
                setOnboardingStep(null);
                cleanUrl();
                setTemplateInitialMode('list');
                setActiveView('dashboard');
              }}
            />
          )}
        </div>
      </div>
    );
  }

  const handleViewChange = (view: ViewType) => {
    if (activeView === 'settings' && view !== 'settings') {
      setSettingsTab('organisation');
    }
    // Bug 3 — Cliquer sur un item de menu = repartir en état propre. Sans ce reset,
    // les query-params locaux à une vue (ex. `?view=profile&supplierId=...` posé par
    // Suppliers) survivent au changement de vue et restaurent un détail fournisseur
    // précédent au retour sur l'item « Fournisseurs ». On ne touche pas à l'URL si la
    // vue est inchangée pour ne pas effacer un deep-link au montage initial.
    if (view !== activeView) {
      window.history.replaceState(null, '', window.location.pathname);
      // Story 3.6 — un changement de vue via menu purge le contexte d'origine
      // du deep-link profil. Sans ce reset, un parcours « profil → campagne →
      // menu Fournisseurs → menu Campagnes → ouvrir un autre fournisseur »
      // ferait pointer le retour vers l'ancien profil orphelin.
      setPendingCampaignTarget(null);
      setSupplierProfileOriginId(null);
    }
    setActiveView(view);
  };

  // Story 5.5 — route renvoyée par le tool `navigateTo` → vue de l'app.
  const AGENT_ROUTE_TO_VIEW: Record<string, ViewType> = {
    '/suppliers': 'suppliers',
    '/campaigns': 'campaigns',
    '/settings': 'settings',
    '/dashboard': 'dashboard',
    '/agents': 'agents',
  };
  const handleAgentNavigate = (route: string) => {
    const view = AGENT_ROUTE_TO_VIEW[route];
    if (view) handleViewChange(view);
  };

  // Story 9.2 — sélection d'un résultat de la palette de recherche globale.
  const handleSearchSelect = (target: SearchTarget) => {
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    switch (target.kind) {
      case 'supplier':
        // handleViewChange efface la query string : écrire l'URL APRÈS.
        handleViewChange('suppliers');
        if (UUID_REGEX.test(target.id)) {
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}?view=profile&supplierId=${target.id}`
          );
        }
        setSearchNavKey((k) => k + 1);
        break;
      case 'campaign':
        handleNavigateToCampaign(target.id);
        break;
      case 'template':
        handleViewChange('forms');
        setTemplateInitialMode('list');
        setPendingTemplatePreviewId(target.id);
        setSearchNavKey((k) => k + 1);
        break;
      case 'conversation':
        handleViewChange('agents');
        break;
    }
  };

  // removed health check effect

  // Auth gate: show login until authenticated
  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Login />
      </div>
    );
  }

  return (
    <AgentsProvider onNavigate={handleAgentNavigate}>
    <SidebarProvider>
      <AppSidebar
        activeView={activeView}
        onViewChange={handleViewChange}
        user={user}
        onLogout={logout}
        onOpenSearch={() => setPaletteOpen(true)}
      />
      <SidebarInset>
        <header className="flex h-14 items-center gap-2 border-b px-4 md:hidden">
          <SidebarTrigger className="-ml-1" aria-label="Ouvrir le menu" />
          <SearchTrigger compact className="ml-auto" onClick={() => setPaletteOpen(true)} />
        </header>
        {activeView === 'agents' ? (
          // Story 5.5 — ChatView pleine hauteur, hors carte générique (scroll interne).
          <main className="flex min-h-0 flex-1 flex-col h-[calc(100svh-3.5rem)] md:h-svh">
            <ChatView />
          </main>
        ) : (
        <main className="flex-1 p-6">
          <div className="bg-white rounded-xl border shadow-sm p-6">
            {activeView === 'dashboard' ? (
              <HeroPanel />
            ) : activeView === 'campaigns' ? (
              <Campaigns
                newCampaignOpen={newCampaignOpen}
                setNewCampaignOpen={setNewCampaignOpen}
                initialTarget={pendingCampaignTarget}
                onTargetConsumed={handleTargetConsumed}
                onBackToSupplierProfile={
                  supplierProfileOriginId ? handleBackToSupplierProfile : undefined
                }
              />
            ) : activeView === 'forms' ? (
              <TemplateLibrary
                key={searchNavKey}
                initialMode={templateInitialMode}
                initialPreviewTemplateId={pendingTemplatePreviewId}
                onInitialPreviewConsumed={() => setPendingTemplatePreviewId(null)}
              />
            ) : activeView === 'suppliers' ? (
              <Suppliers key={searchNavKey} onNavigateToCampaign={handleNavigateToCampaign} />
            ) : activeView === 'settings' ? (
              user.role === 'ADMIN' ? (
                <div className="space-y-4">
                  <div className="flex gap-0 border-b">
                    <button
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        settingsTab === 'organisation'
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => setSettingsTab('organisation')}
                    >
                      Organisation
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        settingsTab === 'utilisateurs'
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => setSettingsTab('utilisateurs')}
                    >
                      Utilisateurs
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        settingsTab === 'compte'
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => setSettingsTab('compte')}
                    >
                      Mon compte
                    </button>
                  </div>
                  {settingsTab === 'organisation' ? (
                    <SettingsOrg />
                  ) : settingsTab === 'utilisateurs' ? (
                    <SettingsUsers />
                  ) : (
                    <AccountSettings />
                  )}
                </div>
              ) : (
                // Story 8.5 — les membres n'ont accès qu'à « Mon compte »
                <AccountSettings />
              )
            ) : (
              <HeroPanel />
            )}
          </div>
        </main>
        )}
      </SidebarInset>
    </SidebarProvider>
    <GlobalCommandPalette
      open={paletteOpen}
      onOpenChange={setPaletteOpen}
      onSelect={handleSearchSelect}
    />
    </AgentsProvider>
  );
}
